import React, { useMemo, forwardRef, useRef, useImperativeHandle, useCallback } from 'react';
import {
  TablePaginatedV8,
  order2Sorting,
  TableCellV8,
  CellCenterV8,
  TableDataState,
  promiseModalDialog,
  toastSuccess,
  CellDateV8,
  EyeColumnV8,
} from 'motion-components';
import { generatePath, useLocation } from 'react-router-dom';
import { useHistory } from 'react-router';

import LoadingOverlay from '@achmadk/react-loading-overlay';
import { useRootStore } from 'store';
import { useTranslation } from 'react-i18next';
import { gridInactiveRowStyle, gridRowSelectedStyle } from 'shared-pages/common/grid-helpfunc';
import * as Lo from 'lodash';
import { FormInput, gqlAdd, gqlDelete, gqlUpdate } from '../graphql';
import { useMutation } from '@apollo/client';
import { getRouteByName } from 'app/routes';
import { BadgeApproval } from '../BadgeApproval';
import { Bullet } from 'shared-pages/components';

/* ----------- Grid ------------------ */

export const DataGrid = forwardRef((props: any, ref) => {
  const {
    data,
    dataCount,
    initPageIndex,
    onPageChange,
    loading,
    handleExport,
    initOrderBy,
    onOrderChange,
    selected,
    setSelected,
    refetch,
    isGridInForm,
  } = props;
  const location = useLocation<any>();
  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t, i18n } = useTranslation();
  const [updateContainer] = useMutation(gqlUpdate);
  const [addNewContainer] = useMutation(gqlAdd);
  const [deleteContainer] = useMutation(gqlDelete);

  const rowClicked = useCallback(
    (row) => {
      history.push({
        pathname: generatePath(getRouteByName('CollectionpointRequest'), { id: row.id }),
        state: {
          // pathname: location.pathname,
          // ...location.state,
        },
      });
    },
    [history, location.pathname, location.state],
  );
  const columns = useMemo(
    () => [
      EyeColumnV8(rowClicked),
      {
        id: 'id',
        header: t('ID'),
        columnName: t('ID'),
        accessorKey: 'id',
        orderByField: 'id',
        cell: CellCenterV8,
        size: 5,
      },
      {
        id: 'firstDate',
        header: t('Ημ/νία Αίτησης'),
        columnName: t('Ημ/νία Αίτησης'),
        accessorKey: 'firstDate',
        orderByField: 'firstDate',
        cell: CellDateV8,
        size: 30,
      },
      {
        id: 'name',
        header: t('Επωνυμία'),
        columnName: t('Επωνυμία'),
        accessorKey: 'name',
        orderByField: 'name',
        cell: TableCellV8,
        size: 40,
        meta: { type: 'text' },
      },
      {
        id: 'professionId',
        header: t('Επάγγελμα'),
        columnName: t('Επάγγελμα'),
        accessorKey: 'profession.name',
        orderByField: 'profession.name',
        cell: TableCellV8,
        size: 30,
        meta: { type: 'text' },
      },
      {
        id: 'addressLine1',
        header: t('Διεύθυνση'),
        columnName: t('Διεύθυνση'),
        accessorKey: 'addressLine1',
        orderByField: 'addressLine1',
        cell: ({ row }) => {
          return (
            <div className="text-left d-flex flex-column">
              <span>{row.original?.addressLine1}</span>
              <span>{row.original?.addressCity}</span>
              <span>{row.original?.postalCode}</span>
              <span>{row.original?.addressArea}</span>
            </div>
          );
        },
        size: 80,
      },

      {
        id: 'phoneNumber1',
        header: t('Τηλέφωνο 1'),
        columnName: t('Τηλέφωνο 1'),
        accessorKey: 'phoneNumber1',
        orderByField: 'phoneNumber1',
        cell: TableCellV8,
        size: 30,
      },
      {
        id: 'requestLines',
        header: t('Τύπος Συσκευασίας'),
        columnName: t('Τύπος Συσκευασίας'),
        accessorKey: 'requestLines',
        orderByField: 'requestLines',
        cell: ({ row }) => {
          //return all the containertype.names
          return (
            <div className="d-flex flex-column  align-items-left">
              {(Array.isArray(row.original?.requestLines) ? row.original?.requestLines : []).map(
                (r: any, idx: number, array: any[]) => {
                  const prefix = array?.length > 1 ? <>{idx + 1}: </> : null;
                  return (
                    <div key={r?.id}>
                      {prefix}
                      {r?.containertype?.name} <Bullet /> {r.quantity ?? 0} {t('τμχ')}
                    </div>
                  );
                },
              )}
            </div>
          );
        },
        size: 30,
      },
      {
        id: 'isApproved',
        header: t('Εγκεκριμένη'),
        columnName: t('Εγκεκριμένη'),
        accessorKey: 'isApproved',
        orderByField: 'isApproved',
        size: 15,
        cell: ({ row }) => {
          return <BadgeApproval approvalStatus={row.original.isApproved} className="pt-4" />;
        },
      },
    ],
    [t],
  );

  const tableRef = useRef(null);
  // return of useRef
  useImperativeHandle(ref, () => (tableRef.current ? tableRef.current : null));

  return (
    <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
      {data ? (
        <TablePaginatedV8
          ref={tableRef}
          loading={loading}
          id="containers-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          data={data}
          dataCount={dataCount}
          topBorderRadius={false}
          onRowClick={(table) => setSelected(table.row.original)}
          rowProps={(row: any) => {
            let style = undefined;
            if (row.original.id === selected?.id) style = gridRowSelectedStyle;
            if (row.original.isActive === false) style = { ...style, ...gridInactiveRowStyle };
            return { style: style };
          }}
          onExport={handleExport}
          onPageChange={onPageChange}
          initPageIndex={initPageIndex}
          initSorting={order2Sorting(initOrderBy)}
          onSortByChange={(sort) => {
            console.log('$$$$ grid sortbychange', sort);
            // $$$
            const columnId = sort[0].id;
            const sortOrder = sort[0].desc ? 'DESC' : 'ASC';
            // find column by id
            const cols = columns.filter((r: any) => r.id === columnId);
            if (cols.length === 1) {
              const col = cols[0] as any;
              let order = col.orderByField ? col.orderByField : 'id';
              order = order + ' ' + sortOrder;
              onOrderChange(order);
            }
          }}
          onNewRow={() => {
            return {
              id: t('*'),
              isActive: true,
            };
          }}
          onSaveRow={async (rowIndex, row, changes) => {
            //
            // i.e. Save Record
            //
            let id = row.id;
            console.log('$$$ Containers - OnSaveRow row', row, changes);
            const values = { id: id, ...changes };
            const mutValues = Lo.pick(values, Lo.keys(new FormInput()));
            // [α] Insert New Record
            if (tableStore.state === TableDataState.insert) {
              mutValues.id = null;
              const res = await addNewContainer({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
              id = res.data.addContainertype.id;
            }
            // ή [β] Update Existing Record
            else if (tableStore.state === TableDataState.edit) {
              const res = await updateContainer({ variables: { data: mutValues } }).catch(
                (reason) => {
                  console.error('$$$ grid.UpdateRow insert error reason', reason);
                },
              );
              if (!res) return false; // some error returned
            }
            refetch();
            return id;
          }}
          deleteRow={async (row) => {
            // mark as deleted
            const ans = await promiseModalDialog({
              title: 'Διαγραφή Εγγραφής',
              text: `<div>Θέλετε να διαγράψετε τον Κάδο <strong>${row.name}</strong>;</div>`,
            });
            if (!ans) {
              return;
            }
            const res = await deleteContainer({ variables: { id: row.id } }).catch((reason) => {
              console.error('$$$ grid.Delete error reason', reason);
            });
            if (!res) {
              return false; // some error returned
            }
            refetch();
            toastSuccess('Διαγράφηκε ο Κάδος');
            return true;
          }}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </LoadingOverlay>
  );
});
